import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Star, MapPin, Video, Home, Building2, Circle, ArrowRight, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { RATINGS_ENABLED } from '../../config/featureFlags';

import { useLanguage } from '../../context/LanguageContext';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODE_ICON = {
  online: Video,
  home: Home,
  center: Building2,
};

/**
 * FeaturedTeachersSection
 * Horizontal scroll of live/available teachers — Airbnb-style cards.
 * Fetches /api/public/featured-teachers (falls back to /teachers?limit=8).
 */
const FeaturedTeachersSection = ({ language = 'ar', isDark = false }) => {
  const { localize } = useLanguage();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAr = language === 'ar';
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollerRef = useRef(null);

  useEffect(() => {
    const fetchTeachers = async () => {
      try {
        // Try featured endpoint first
        try {
          const r = await axios.get(`${API}/public/featured-teachers`, { params: { limit: 8 } });
          if (Array.isArray(r.data) && r.data.length > 0) {
            setTeachers(r.data);
            return;
          }
        } catch { /* fallthrough */ }

        // Fallback: generic teachers list
        const r2 = await axios.get(`${API}/teachers`, { params: { limit: 8 } });
        setTeachers(Array.isArray(r2.data) ? r2.data.slice(0, 8) : []);
      } catch {
        setTeachers([]);
      } finally {
        setLoading(false);
      }
    };
    fetchTeachers();
  }, []);

  // Arrow scroll helper
  const scrollBy = (offset) => {
    scrollerRef.current?.scrollBy({ left: isAr ? -offset : offset, behavior: 'smooth' });
  };

  // Placeholder skeletons during initial load
  const skeletonCount = 4;

  if (!loading && teachers.length === 0) return null;  // hide section if no data

  return (
    <section
      className={`py-10 sm:py-16 lg:py-24 ${isDark ? 'bg-[#0d1117]' : 'bg-white'}`}
      dir={isAr ? 'rtl' : 'ltr'}
      data-testid="featured-teachers-section"
    >
      <div className="max-w-6xl mx-auto px-6">
        {/* Header row */}
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <span className="inline-block text-xs font-bold tracking-widest uppercase text-indigo-600 mb-2">
              {localize({ ar: 'الأفضل الآن', en: 'Top rated now' })}
            </span>
            <h2 className={`text-3xl lg:text-4xl font-black tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {localize({ ar: 'أفضل المدرسين الآن', en: 'Top tutors right now' })}
            </h2>
          </div>

          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={() => scrollBy(-380)}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${isDark ? 'bg-[#161b22] text-slate-300 hover:bg-[#1f2937]' : 'bg-slate-100 text-slate-600 hover:bg-indigo-100 hover:text-indigo-700'}`}
              aria-label="previous"
              data-testid="featured-prev-btn"
            >
              {isAr ? <ArrowRight className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
            </button>
            <button
              onClick={() => scrollBy(380)}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${isDark ? 'bg-[#161b22] text-slate-300 hover:bg-[#1f2937]' : 'bg-slate-100 text-slate-600 hover:bg-indigo-100 hover:text-indigo-700'}`}
              aria-label="next"
              data-testid="featured-next-btn"
            >
              {isAr ? <ArrowLeft className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Scrollable row */}
        <div
          ref={scrollerRef}
          className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory -mx-6 px-6 no-scrollbar"
          style={{ scrollbarWidth: 'none' }}
        >
          {loading
            ? [...Array(skeletonCount)].map((_, i) => (
                <TeacherSkeleton key={`sk-${i}`} isDark={isDark} />
              ))
            : teachers.map((t, i) => (
                <TeacherCard
                  key={t.id || t._id || i}
                  teacher={t}
                  isAr={isAr}
                  isDark={isDark}
                  onClick={() => {
                    const id = t.id || t._id;
                    navigate(user ? `/student/teacher/${id}` : `/tutors`);
                  }}
                />
              ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Card ──────────────────────────────────────────────────── */
const TeacherCard = ({ teacher, isAr, isDark, onClick }) => {
  const name = teacher.user_name || teacher.name || 'Tutor';
  const subjects = Array.isArray(teacher.subjects) ? teacher.subjects : [];
  const rating = Number(teacher.average_rating || teacher.rating || 0);
  const price = teacher.hourly_rate || teacher.price_per_hour || teacher.price;
  const picture = teacher.profile_picture || teacher.user_picture;
  const available = teacher.available_now || teacher.is_available || false;
  const modes = teacher.available_modes || ['online']; // 'online' | 'home' | 'center'
  const city = teacher.emirate || teacher.city;

  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2 }}
      className={`flex-shrink-0 w-[280px] snap-start text-start rounded-2xl overflow-hidden border transition-all ${
        isDark
          ? 'bg-[#161b22] border-[#30363d] hover:border-indigo-600/50'
          : 'bg-white border-slate-100 hover:border-indigo-200 hover:shadow-[0_16px_40px_rgba(79,70,229,0.12)]'
      } shadow-[0_2px_10px_rgba(15,23,42,0.04)]`}
      data-testid={`featured-teacher-card-${teacher.id || teacher._id || 'x'}`}
    >
      {/* Picture area */}
      <div className={`relative h-40 ${isDark ? 'bg-[#0d1117]' : 'bg-slate-100'} overflow-hidden`}>
        {picture ? (
          <img src={picture} alt={name} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-indigo-100 to-indigo-200">
            <span className="text-6xl font-black text-indigo-400">{name.charAt(0)}</span>
          </div>
        )}

        {/* Available badge */}
        {available && (
          <div className="absolute top-3 start-3 inline-flex items-center gap-1.5 bg-emerald-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow">
            <Circle className="w-2 h-2 fill-white text-white animate-pulse" />
            {localize({ ar: 'متاح الآن', en: 'Available now' })}
          </div>
        )}

        {/* Mode icons */}
        <div className="absolute bottom-3 end-3 flex items-center gap-1">
          {modes.map((m) => {
            const Icon = MODE_ICON[m] || Video;
            return (
              <span key={m} className="w-7 h-7 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-sm">
                <Icon className="w-3.5 h-3.5 text-indigo-600" />
              </span>
            );
          })}
        </div>
      </div>

      {/* Body */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <h3 className={`font-bold text-sm truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>{name}</h3>
          {RATINGS_ENABLED && rating > 0 && (
            <div className="flex items-center gap-0.5 flex-shrink-0">
              <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
              <span className={`text-xs font-bold ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
                {rating.toFixed(1)}
              </span>
            </div>
          )}
        </div>

        {subjects.length > 0 && (
          <p className={`text-xs truncate mb-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            {subjects.slice(0, 2).join(' • ')}
          </p>
        )}

        {city && (
          <div className={`flex items-center gap-1 text-[11px] mb-3 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
            <MapPin className="w-3 h-3" />
            {city}
          </div>
        )}

        <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-[#30363d]">
          <span className={`text-xs font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {price ? (
              <>
                {price} <span className={`font-normal text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                  {localize({ ar: 'د.إ/ساعة', en: 'AED/hr' })}
                </span>
              </>
            ) : (
              localize({ ar: 'تواصل', en: 'Contact' })
            )}
          </span>
          <span className="inline-flex items-center gap-1 text-[11px] font-bold text-indigo-600 group-hover:gap-2 transition-all">
            {localize({ ar: 'احجز الآن', en: 'Book' })}
            <ArrowRight className={`w-3 h-3 ${isAr ? 'rotate-180' : ''}`} />
          </span>
        </div>
      </div>
    </motion.button>
  );
};

/* ─── Skeleton during load ──────────────────────────────────── */
const TeacherSkeleton = ({ isDark }) => (
  <div
    className={`flex-shrink-0 w-[280px] snap-start rounded-2xl overflow-hidden border animate-pulse ${
      isDark ? 'bg-[#161b22] border-[#30363d]' : 'bg-white border-slate-100'
    }`}
  >
    <div className={`h-40 ${isDark ? 'bg-[#30363d]' : 'bg-slate-200'}`} />
    <div className="p-4 space-y-3">
      <div className={`h-4 w-3/4 rounded ${isDark ? 'bg-[#30363d]' : 'bg-slate-200'}`} />
      <div className={`h-3 w-1/2 rounded ${isDark ? 'bg-[#30363d]' : 'bg-slate-200'}`} />
      <div className={`h-3 w-1/3 rounded ${isDark ? 'bg-[#30363d]' : 'bg-slate-200'}`} />
      <div className="flex justify-between pt-2">
        <div className={`h-4 w-16 rounded ${isDark ? 'bg-[#30363d]' : 'bg-slate-200'}`} />
        <div className={`h-4 w-10 rounded ${isDark ? 'bg-[#30363d]' : 'bg-slate-200'}`} />
      </div>
    </div>
  </div>
);

export default FeaturedTeachersSection;
